/**
 * Pix eligibility + IB home account selection.
 *
 * Instance shells (OPERATING / OVERDRAFT / PROGRAM / ENTITY ROOT litter)
 * stay off Carteira home. Exit-rail virtuals (WALLET / OTC) are home-selectable and
 * can run Pix/ops via the Root PaySmart rail (ledger stays on the virtual).
 *
 * Align with MobileService IbPersonAccountService / baasVirtualShells /
 * BankData ExitRailVirtualAccounts:
 * role=root alone is NOT a shell — program owner (VALUT GLOBAL LLC) may be RootAccountId.
 */

import {
  isBaasExitRailVirtualAccount,
  isBaasSegregationWalletAccount,
  isVirtualInstanceShellAccount,
} from "../Services/baasVirtualShells";

const ALWAYS_VIRTUAL_SHELL_ROLES = new Set([
  "operating",
  "program_reserve",
  "overdraft_reserve",
  "reserve",
]);

const VIRTUAL_SHELL_NAME_RE =
  /^(ROOT(?:\s+ACCOUNT)?|OPERATING|OVERDRAFT_RESERVE|PROGRAM_RESERVE)$/i;

const resolveAccountRole = (accountOrType) =>
  String(
    typeof accountOrType === "string"
      ? accountOrType
      : accountOrType?.role
        || accountOrType?.type
        || accountOrType?.baasAccountType
        || accountOrType?.raw?.role
        || accountOrType?.raw?.account_type
        || "",
  )
    .trim()
    .toLowerCase();

const nameLooksLikeShell = (account) => {
  if (!account || typeof account === "string") return false;
  const candidates = [
    account?.name,
    account?.alias,
    account?.entityName,
    account?.raw?.name,
  ];
  return candidates.some((value) =>
    VIRTUAL_SHELL_NAME_RE.test(String(value || "").trim()),
  );
};

/** Instance ledger shells — not IB home / Pix product accounts. */
export const isVirtualAccountType = (accountOrType) => {
  if (!accountOrType) return false;

  if (accountOrType && typeof accountOrType === "object") {
    if (isBaasExitRailVirtualAccount(accountOrType)) return false;
    if (typeof isVirtualInstanceShellAccount === "function") {
      if (isVirtualInstanceShellAccount(accountOrType)) return true;
    }
    if (nameLooksLikeShell(accountOrType)) return true;
    if (accountOrType.is_virtual === true || accountOrType.isVirtual === true) {
      return nameLooksLikeShell(accountOrType);
    }
  }

  const role = resolveAccountRole(accountOrType);
  if (ALWAYS_VIRTUAL_SHELL_ROLES.has(role)) return true;
  // `role`/`type` "root" is the real single instance Root pin — never a shell.
  // VIRTUAL_SHELL_NAME_RE targets ENTITY setup litter *names* (which legitimately
  // include "ROOT"); testing it against the role string here would otherwise
  // re-flag every Root account (program owner included) as a virtual shell.
  if (role === "root") return false;
  return VIRTUAL_SHELL_NAME_RE.test(role);
};

/** @deprecated Prefer isVirtualAccountType */
export const isVirtualShellAccount = isVirtualAccountType;

/**
 * IB home account picker — real PaySmart accounts + exit-rail virtuals (WALLET/OTC).
 * Shells stay excluded.
 */
export const isIbHomeSelectableAccount = (account) => {
  if (!account) return false;
  if (isVirtualAccountType(account)) return false;
  if (account.is_virtual === true || account.isVirtual === true) {
    // Ambiguous flag: allow only when clearly an exit-rail virtual.
    return isBaasExitRailVirtualAccount(account);
  }
  return true;
};

/**
 * Pix page / keys — shells blocked; exit-rail virtuals allowed (Root rail).
 */
export const isPixEligibleAccount = (account) => {
  if (!account) return false;
  if (isVirtualAccountType(account)) return false;
  if (account.canPix === false || account.can_pix === false) return false;
  const features = account.features || account.raw?.features;
  if (features && features.pix === false) return false;
  if (isBaasExitRailVirtualAccount(account) || isBaasSegregationWalletAccount(account)) {
    return true;
  }
  return true;
};

export const canAccountUsePix = isPixEligibleAccount;
